import {ThemeToggle} from "@src/features/settings/components/theme-toggle";
import {useSelectedSpace} from "@src/features/space-home/hooks/use-selected-space";
import { useAppSelector } from "@src/integrations/redux/redux-store";
import {OptionalAccountMenu} from "@src/ui/compoments/account-menu";
import { selectUserFullInfo } from "@src/features/auth/redux/auth-slice";
import {Avatar, AvatarFallback, AvatarImage} from "@src/ui/views/avatar";
import {Button} from "@src/ui/views/button";
import {Separator} from "@src/ui/views/separator";
import {Sheet, SheetContent, SheetTrigger} from "@src/ui/views/sheet";
import {Tooltip, TooltipContent, TooltipTrigger} from "@src/ui/views/tooltip";
import {Gauge, Home, Menu, Bug} from "lucide-react";
import React from "react";
import {NavLink, Outlet, type To} from "react-router-dom";
import {default as cherriLogo} from "resources/app-logo/app-logo-246x246.webp";


export function TopbarLayout() {
	return (
		<div className="h-screen w-screen flex flex-col overflow-x-clip">
			<header
				className="sticky flex-none top-0 flex h-16 items-center justify-between gap-4 border-b bg-background px-4 md:px-6 z-topbar text-sm font-medium lg:text-base">
				<SideSheet/>
				<NavBar/>
				<div className="h-full flex flex-row items-center justify-end gap-4">
					<Separator orientation="vertical"/>
					<ThemeToggle/>
					<OptionalAccountMenu/>
				</div>
			</header>
			<main className="h-full gap-4 p-4 md:gap-8 md:p-8 sm:px-6 sm:py-0 overflow-y-auto">
				<Outlet/>
			</main>
		</div>
	);
}


function SideSheet() {
	return (
		<Sheet>
			<SheetTrigger asChild>
				<Button
					variant="outline"
					size="icon"
					className="shrink-0 md:hidden"
				>
					<Menu className="size-5"/>
					<span className="sr-only">Toggle navigation menu</span>
				</Button>
			</SheetTrigger>
			<SheetContent side="left">
				<nav className="grid gap-6 text-lg font-medium">
					<IconLink
						to="/"
						alt="Home"
					>
						<Avatar>
							<AvatarImage
								src={cherriLogo}
								alt="Home"
							/>
							<AvatarFallback>
								<Home/>
							</AvatarFallback>
						</Avatar>
					</IconLink>
					<Separator orientation="horizontal"/>
					<SpaceLinks useSmaller/>
				</nav>
			</SheetContent>
		</Sheet>
	);
}


function NavBar() {
	return (
		<nav className="hidden flex-row items-center gap-5 text-sm font-medium md:flex lg:gap-6">
			<IconLink
				to="/"
				alt="Home"
			>
				<Avatar>
					<AvatarImage
						src={cherriLogo}
						alt="Home"
					/>
					<AvatarFallback>
						<Home/>
					</AvatarFallback>
				</Avatar>
			</IconLink>
			<SpaceLinks/>
		</nav>
	);
}


function SpaceLinks(props: { useSmaller?: boolean }) {
	const selectedSpace = useSelectedSpace();
  const user = useAppSelector(selectUserFullInfo);
	if (!selectedSpace) {
		return null;
	}
	
	return (
		<>
			<IconLink
				to={`/dashboard/${selectedSpace.spaceId}`}
				alt={`${selectedSpace.name}'s Dashboard`}
			>
				<Avatar>
					{
						selectedSpace.pictureUrl && (
							<AvatarImage
								src={selectedSpace.pictureUrl}
								alt={`${selectedSpace.name}'s Dashboard`}
							/>
						)
					}
					<AvatarFallback>
						<Gauge/>
					</AvatarFallback>
				</Avatar>
			</IconLink>
			<Link1 to={`/dashboard/${selectedSpace.spaceId}/live-shuttles`}>
				Live Shuttles
			</Link1>
			<Link1 to={`/dashboard/${selectedSpace.spaceId}/timed-shuttles`}>
				Timed Shuttles
			</Link1>
			<Link1 to={`/dashboard/${selectedSpace.spaceId}/ride-share`}>
				Ride Share
			</Link1>
			<Link1 to={`/dashboard/${selectedSpace.spaceId}/welfare`}>
				Welfare
			</Link1>
			<Link1 to={`/dashboard/${selectedSpace.spaceId}/users`}>Users</Link1>
			<Link1 to={`/dashboard/${selectedSpace.spaceId}/route-timed-shuttle`}>
				Route Timed Shuttle
			</Link1>
				{/* only show for your userId */}
			{user?.userId === "izuhVI2Yv" && (
			<NavLink
			to={`/dashboard/${selectedSpace.spaceId}/debug`}
			className={({ isActive }) =>
				isActive
				? "text-foreground flex items-center gap-1"
				: "text-muted-foreground hover:text-foreground flex items-center gap-1"
			}
			>
			<Bug className="h-4 w-4" />
			Debug
			</NavLink> )}
		</>
	);
	
}


function Link1(props: React.PropsWithChildren<{ to: To, end?: boolean }>) {
	return (
		<NavLink
			to={props.to}
			end={props.end}
			className={(state) => {
				if (state.isActive) {
					return "text-foreground transition-colors";
				} else {
					return "text-muted-foreground transition-colors hover:text-foreground";
				}
			}}
		>
			{props.children}
		</NavLink>
	);
}


function IconLink(props: React.PropsWithChildren<{ to: To, end?: boolean, alt: string }>) {
	// todo in drawer the set icon size to 6x6. If it's important, make sure to account for that
	return (
		<NavLink
			to={props.to}
			end={props.end}
			className="flex items-center gap-2 text-lg font-semibold md:text-base"
		>
			<Tooltip>
				<TooltipTrigger asChild>
					<Button variant="ghost" size="icon" className="rounded-full overflow-clip">
						{props.children}
						<span className="sr-only">{props.alt}</span>
					</Button>
				</TooltipTrigger>
				<TooltipContent>
					<p>{props.alt}</p>
				</TooltipContent>
			</Tooltip>
		</NavLink>
	);
}
